import { DatePipe, UpperCasePipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	AlertComponent,
	ButtonIconComponent,
	DividerComponent,
	IMenu,
	InfoGroupComponent,
	InputComponent,
	ItemComponent,
	ListDetailComponent,
	MenuComponent,
	ModalComponent,
	SwitchComponent,
	TagComponent,
	TileComponent,
} from '@tecalisdev/infinity';
import { Permission } from '../../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { MenuItemAction } from '../../../../../shared/models/master.models';
import { LoadingService } from '../../../../../shared/services/loading.service';
import {
	parseRetentionDuration,
	RetentionDurationApiValue,
	retentionDurationDisplayKey,
} from '../../../../../shared/utils/retention-duration.util';
import { ICompaniesDetailBody, ICompaniesDetailResponse, ICompaniesListBody } from '../../models/companies.models';
import { CompaniesHelperService } from '../../services/companies-helper.service';
import { CompaniesService } from '../../services/companies.service';

@Component({
	selector: 'app-companies-detail',
	imports: [
		UpperCasePipe,
		TranslatePipe,
		ModalComponent,
		AlertComponent,
		TileComponent,
		DividerComponent,
		TagComponent,
		ItemComponent,
		InfoGroupComponent,
		ListDetailComponent,
		MenuComponent,
		ButtonIconComponent,
		SwitchComponent,
		InputComponent,
	],
	providers: [DatePipe],
	templateUrl: './companies-detail.component.html',
	styleUrl: './companies-detail.component.scss',
})
export class CompaniesDetailComponent implements OnInit {
	private loading = inject(LoadingService);
	private trans = inject(TranslateService);
	private companiesService = inject(CompaniesService);
	private companiesHelper = inject(CompaniesHelperService);
	private rolePermissions = inject(RolePermissionsService);
	private datePipe = inject(DatePipe);

	/** UUID para cargar el detalle. */
	@Input() id?: string;
	/** Fila del listado (fallback de menú si el detalle aún no cargó). */
	@Input() company?: ICompaniesListBody | null;

	@Output() closeEvent = new EventEmitter<void>();
	@Output() editEvent = new EventEmitter<void>();
	@Output() menuChange = new EventEmitter<IMenu>();

	public detail?: ICompaniesDetailBody;
	public errorMsg = '';
	public menuOptions: IMenu[] = [];
	public createdAtLabel = '';

	ngOnInit(): void {
		if (this.id) {
			this.getDetail();
		}
	}

	get editButtonLabel(): string | undefined {
		return this.rolePermissions.canEditCompany() ? this.trans.instant('BUTTON.EDIT') : undefined;
	}

	private getDetail(): void {
		this.loading.show(this.trans.instant('LOADING.GET_COMPANY'), false, false);
		this.companiesService.detail(this.id).subscribe({
			next: (response: ICompaniesDetailResponse) => {
				this.detail = response.data;
				this.createdAtLabel =
					this.datePipe.transform(this.detail.created_at, 'dd/MM/yyyy - HH:mm') ?? '';
				this.menuOptions = this.buildMenuOptions(this.menuCompanyRow());
			},
			error: () => {
				this.errorMsg = this.trans.instant(`COMPANIES.LIST.DETAIL_ERROR`);
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private buildMenuOptions(row: ICompaniesListBody | null): IMenu[] {
		if (!row) {
			return [];
		}
		const options = [...this.companiesHelper.getDetailMenuOpt(row)];
		if (this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)) {
			const curlItem: IMenu = {
				label: this.trans.instant('COMPANIES.LIST.MENU_CURL_REQUEST'),
				icon: 'icon-code-brackets',
				id: MenuItemAction.SHOW_CURL,
			};
			const deleteIdx = options.findIndex((o) => o.id === MenuItemAction.DELETE);
			if (deleteIdx >= 0) {
				options.splice(deleteIdx, 0, curlItem);
			} else {
				options.push(curlItem);
			}
		}
		return options;
	}

	private menuCompanyRow(): ICompaniesListBody | null {
		if (!this.detail) {
			return this.company ?? null;
		}
		return {
			uuid: this.detail.uuid,
			name: this.detail.name,
			code: this.detail.code,
			country: this.detail.country,
			enabled: this.detail.enabled,
			created_at: this.detail.created_at,
			updated_at: this.detail.updated_at,
			manual_validation: this.detail.manual_validation,
			document_retention_days: this.detail.document_retention_days,
			media_retention_days: this.detail.media_retention_days,
			info_retention_days: this.detail.info_retention_days,
			billing_id: this.detail.billing_id,
		};
	}

	public onModalClose(type: string | null): void {
		if (type === 'buttonSecondary') {
			this.editEvent.emit();
			return;
		}
		this.closeEvent.emit();
	}

	public onMenuChange(action: IMenu): void {
		this.menuChange.emit(action);
	}

	public formatRetention(value: RetentionDurationApiValue | number | undefined): string {
		const parts = parseRetentionDuration(value);
		const key = retentionDurationDisplayKey(parts);
		if (parts.unit === 'none' || parts.unit === 'immediate') {
			return this.trans.instant(key);
		}
		return this.trans.instant(key, { value: parts.amount });
	}
}
